// Blur Gallery: Path Blur and Spin Blur.
// Path Blur — motion blur along a user-defined path.
// Spin Blur — radial motion blur around a center point.

import { PixelBuffer } from "../pixel/PixelBuffer";
import type { Selection } from "../selection/Selection";
import type { Filter } from "./Filter";

export type BlurGalleryMode = "path-blur" | "spin-blur";

export interface BlurGalleryParams {
  mode: BlurGalleryMode;
  // Path Blur params
  path: { x: number; y: number }[]; // array of points defining the blur path
  pathBlurAmount: number; // 0..100, default 30
  pathTaper: number; // 0..100, default 0
  pathCenterMotion: boolean; // whether center has motion
  // Spin Blur params
  centerX: number; // 0..1, relative position
  centerY: number; // 0..1, relative position
  spinAngle: number; // degrees, default 30
  spinRadius: number; // 0..1, relative radius
  spinTaper: number; // 0..100, default 0
  // Shared
  step: number; // sampling step, default 12
}

function clamp8(v: number): number {
  return v < 0 ? 0 : v > 255 ? 255 : v;
}

function applyPathBlur(
  src: Uint8ClampedArray,
  dst: Uint8ClampedArray,
  w: number,
  h: number,
  params: BlurGalleryParams,
): void {
  const { path, pathBlurAmount, pathTaper, step = 12 } = params;

  if (path.length < 2) {
    dst.set(src);
    return;
  }

  // Compute path direction at each point
  const pathDirs: { dx: number; dy: number; len: number }[] = [];
  for (let i = 0; i < path.length; i++) {
    let dx = 0, dy = 0;
    if (i === 0) {
      dx = path[1]!.x - path[0]!.x;
      dy = path[1]!.y - path[0]!.y;
    } else if (i === path.length - 1) {
      dx = path[i]!.x - path[i - 1]!.x;
      dy = path[i]!.y - path[i - 1]!.y;
    } else {
      dx = (path[i + 1]!.x - path[i - 1]!.x) / 2;
      dy = (path[i + 1]!.y - path[i - 1]!.y) / 2;
    }
    const len = Math.sqrt(dx * dx + dy * dy) || 1;
    pathDirs.push({ dx: dx / len, dy: dy / len, len });
  }

  const blurPx = pathBlurAmount;
  const n = w * h;

  for (let i = 0; i < n; i++) {
    const px = i % w;
    const py = Math.floor(i / w);
    const o = i * 4;

    // Find closest path point
    let minDist = Infinity;
    let closestIdx = 0;
    for (let j = 0; j < path.length; j++) {
      const dx = px - path[j]!.x;
      const dy = py - path[j]!.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < minDist) {
        minDist = dist;
        closestIdx = j;
      }
    }

    // Blur amount decreases with distance from path (taper)
    const taperRadius = blurPx * 3;
    if (minDist > taperRadius) {
      dst[o] = src[o]!;
      dst[o + 1] = src[o + 1]!;
      dst[o + 2] = src[o + 2]!;
      dst[o + 3] = src[o + 3]!;
      continue;
    }

    const distFactor = 1 - minDist / taperRadius;
    const effectiveBlur = blurPx * distFactor * (1 - pathTaper / 100 * (closestIdx / Math.max(path.length - 1, 1)));

    if (effectiveBlur < 1) {
      dst[o] = src[o]!;
      dst[o + 1] = src[o + 1]!;
      dst[o + 2] = src[o + 2]!;
      dst[o + 3] = src[o + 3]!;
      continue;
    }

    // Sample along path direction
    const dir = pathDirs[closestIdx]!;
    let sumR = 0, sumG = 0, sumB = 0, sumA = 0, count = 0;
    const steps = Math.ceil(effectiveBlur / step);

    for (let s = -steps; s <= steps; s++) {
      const sx = px + dir.dx * s * step;
      const sy = py + dir.dy * s * step;

      if (sx < 0 || sx >= w || sy < 0 || sy >= h) continue;

      const si = (Math.floor(sy) * w + Math.floor(sx)) * 4;
      sumR += src[si]!;
      sumG += src[si + 1]!;
      sumB += src[si + 2]!;
      sumA += src[si + 3]!;
      count++;
    }

    if (count > 0) {
      dst[o] = Math.round(sumR / count);
      dst[o + 1] = Math.round(sumG / count);
      dst[o + 2] = Math.round(sumB / count);
      dst[o + 3] = Math.round(sumA / count);
    } else {
      dst[o] = src[o]!;
      dst[o + 1] = src[o + 1]!;
      dst[o + 2] = src[o + 2]!;
      dst[o + 3] = src[o + 3]!;
    }
  }
}

function applySpinBlur(
  src: Uint8ClampedArray,
  dst: Uint8ClampedArray,
  w: number,
  h: number,
  params: BlurGalleryParams,
): void {
  const { centerX = 0.5, centerY = 0.5, spinAngle, spinRadius = 0.5, spinTaper, step = 12 } = params;

  const cx = centerX * w;
  const cy = centerY * h;
  const maxRadius = spinRadius * Math.max(w, h);
  const angleRad = (spinAngle * Math.PI) / 180;
  const n = w * h;

  for (let i = 0; i < n; i++) {
    const px = i % w;
    const py = Math.floor(i / w);
    const o = i * 4;

    const dx = px - cx;
    const dy = py - cy;
    const dist = Math.sqrt(dx * dx + dy * dy);

    // Outside radius: no blur
    if (dist > maxRadius) {
      dst[o] = src[o]!;
      dst[o + 1] = src[o + 1]!;
      dst[o + 2] = src[o + 2]!;
      dst[o + 3] = src[o + 3]!;
      continue;
    }

    // Angle increases with distance from center, taper reduces at edges
    const distFactor = dist / maxRadius;
    const taperFactor = spinTaper > 0 ? 1 - spinTaper / 100 * distFactor : 1;
    const localAngle = angleRad * distFactor * taperFactor;

    if (Math.abs(localAngle) < 0.01) {
      dst[o] = src[o]!;
      dst[o + 1] = src[o + 1]!;
      dst[o + 2] = src[o + 2]!;
      dst[o + 3] = src[o + 3]!;
      continue;
    }

    // Sample along arc
    const cosA = Math.cos(localAngle);
    const sinA = Math.sin(localAngle);
    let sumR = 0, sumG = 0, sumB = 0, sumA = 0, count = 0;

    // Number of samples based on angle magnitude
    const numSamples = Math.max(3, Math.ceil(Math.abs(localAngle) / (step / Math.max(dist, 1))));

    for (let s = 0; s < numSamples; s++) {
      const t = (s / (numSamples - 1)) * 2 - 1; // -1 to 1
      const a = localAngle * t;
      const ca = Math.cos(a);
      const sa = Math.sin(a);

      // Rotate point around center
      const rx = cx + dx * ca - dy * sa;
      const ry = cy + dx * sa + dy * ca;

      if (rx < 0 || rx >= w || ry < 0 || ry >= h) continue;

      const si = (Math.floor(ry) * w + Math.floor(rx)) * 4;
      sumR += src[si]!;
      sumG += src[si + 1]!;
      sumB += src[si + 2]!;
      sumA += src[si + 3]!;
      count++;
    }

    if (count > 0) {
      dst[o] = Math.round(sumR / count);
      dst[o + 1] = Math.round(sumG / count);
      dst[o + 2] = Math.round(sumB / count);
      dst[o + 3] = Math.round(sumA / count);
    } else {
      dst[o] = src[o]!;
      dst[o + 1] = src[o + 1]!;
      dst[o + 2] = src[o + 2]!;
      dst[o + 3] = src[o + 3]!;
    }
  }
}

export const BlurGallery: Filter<BlurGalleryParams> = {
  id: "blur-gallery",
  displayName: "Blur Gallery",
  defaultParams: {
    mode: "path-blur",
    path: [{ x: 100, y: 100 }, { x: 300, y: 200 }],
    pathBlurAmount: 30,
    pathTaper: 0,
    pathCenterMotion: true,
    centerX: 0.5,
    centerY: 0.5,
    spinAngle: 30,
    spinRadius: 0.5,
    spinTaper: 0,
    step: 12,
  },

  apply(
    input: PixelBuffer,
    params: BlurGalleryParams,
    _selection: Selection | null,
  ): PixelBuffer {
    const w = input.width;
    const h = input.height;
    const out = new PixelBuffer(w, h, input.mode);
    const src = input.data;
    const dst = out.data;

    if (w === 0 || h === 0) {
      dst.set(src);
      return out;
    }

    switch (params.mode) {
      case "path-blur":
        applyPathBlur(src, dst, w, h, params);
        break;
      case "spin-blur":
        applySpinBlur(src, dst, w, h, params);
        break;
    }

    return out;
  },
};
